import React, { useState, useEffect, useRef } from 'react';
import {
    Box, Typography, Button, TextField, Grid, Paper,
    IconButton, Select, MenuItem, FormControl, InputLabel, Slider, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert,
    Accordion, AccordionSummary, AccordionDetails, Tooltip,
    Dialog, DialogContent, Fade, Zoom
} from '@mui/material';
import {
    IconPlus, IconTrash, IconChevronDown, IconUpload,
    IconX, IconCheck, IconArrowLeft, IconArrowRight, IconBuilding,
    IconUsers, IconChartBar, IconCurrencyDollar, IconForms, IconFileText,
    IconInfoCircle, IconShieldCheck, IconTarget, IconFlag, IconPackage, IconPercentage,
    IconArrowUp
} from '@tabler/icons-react';
import { useForm, Controller, useFieldArray, set } from 'react-hook-form';
import PageContainer from './Pagecontainer';
import axios from 'axios';
import { BeatLoader } from 'react-spinners';

const steps = [
    { label: 'Companies & Products', icon: <IconBuilding size={18} /> },
    { label: 'Valuation Settings', icon: <IconChartBar size={18} /> },
    { label: 'Fundraising', icon: <IconCurrencyDollar size={18} /> },
    { label: 'Results / Summary', icon: <IconUsers size={18} /> }
];

const stageOptions = ['Beta', 'Early Traction', 'Commercial', 'Mature'];
const companyStageOptions = ['Pre-seed', 'Seed', 'Series A-ready', 'Series A', 'Series B+'];

const defaultProduct = {
    name: '', description: '', category: '', stage: '', launchDate: '',
    currentARR: '', currentMRR: '', payingCustomers: '', arpu: '', growthRate: '',
    year1ARR: '', year2ARR: '', year3ARR: '', tam: '', sam: '', revenueMultiple: '',
    riskFactor: 1, strategicValue: 0
};

const defaultFounder = { name: '', ownership: '' };
const defaultInvestor = { name: '', ownership: '', investmentDate: '', amount: '' };

const defaultCompany = {
    name: '', legalStructure: '', vision: '', platformStrategy: '',
    foundingDate: '', incorporationState: '', currentStage: '',
    ipOwnership: '', founders: [], existingInvestors: [], esopPool: '',
    keyTeamMembers: '', exitStrategy: '', milestone12Month: '', milestone18Month: '',
    expanded: true, products: []
};

const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

const useOfFundsLabels = { productDev: 'Product Development', salesMarketing: 'Sales & Marketing', teamHiring: 'Team & Hiring', operations: 'Operations & Buffer' };

const parseCurrency = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;
};

const parsePercent = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    return parseFloat(String(value).replace(/[^0-9.-]/g, '')) || 0;
};

const parseCustomerCount = (value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const match = String(value).match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
};

const mapCompanyStage = (stage) => {
    if (!stage) return 'Seed';
    const lower = stage.toLowerCase();
    if (lower.includes('pre-seed') || lower.includes('pre seed')) return 'Pre-seed';
    if (lower.includes('series b') || lower.includes('series c') || lower.includes('series d')) return 'Series B+';
    if (lower.includes('series a-ready') || lower.includes('series a ready')) return 'Series A-ready';
    if (lower.includes('series a')) return 'Series A';
    if (lower.includes('seed')) return 'Seed';
    return 'Seed';
};

const mapProductStage = (stage) => {
    if (!stage) return 'Beta';
    const lower = stage.toLowerCase();
    if (lower === 'beta') return 'Beta';
    if (lower === 'early traction') return 'Early Traction';
    if (lower === 'commercial' || lower === 'growth') return 'Commercial';
    if (lower === 'mature') return 'Mature';
    return 'Beta';
};

export default function ProductValuation() {
    const [activeStep, setActiveStep] = useState(0);
    const [topLevelDocument, setTopLevelDocument] = useState(null);
    const [documentData, setDocumentData] = useState([])
    const [loading, setLoading] = useState(false);
    const [productUploadingIdx, setProductUploadingIdx] = useState(null);
    const [addCompanyModalOpen, setAddCompanyModalOpen] = useState(false);
    const [companyData, setCompanyData] = useState([]);
    const topLevelFileInputRef = useRef(null);
    const [sessionID,setSessionID] = useState(null);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        let scrollParent = el.parentElement;
        while (scrollParent) {
            const style = window.getComputedStyle(scrollParent);
            if (style.overflow === 'auto' || style.overflow === 'scroll' ||
                style.overflowY === 'auto' || style.overflowY === 'scroll') {
                break;
            }
            scrollParent = scrollParent.parentElement;
        }
        if (!scrollParent) return;

        const handleScroll = () => {
            setShowScrollTop(scrollParent.scrollTop > window.innerHeight);
        };
        scrollParent.addEventListener('scroll', handleScroll);
        return () => scrollParent.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        const el = scrollContainerRef.current;
        if (!el) return;
        let scrollParent = el.parentElement;
        while (scrollParent) {
            const style = window.getComputedStyle(scrollParent);
            if (style.overflow === 'auto' || style.overflow === 'scroll' ||
                style.overflowY === 'auto' || style.overflowY === 'scroll') {
                break;
            }
            scrollParent = scrollParent.parentElement;
        }
        if (scrollParent) {
            scrollParent.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const { control, register: formRegister, watch, setValue, getValues, handleSubmit, formState: { errors } } = useForm({
        defaultValues: {

            companies: [],
            valuationParams: {
                ffDiscountPercent: 65, ffRevenueMultiple: 3, vcRevenueMultiple: 12, platformPremium: 20, executionPremium: 15,
                ffStageMultipliers: { Beta: 0.3, 'Early Traction': 0.5, Commercial: 1.0, Mature: 1.2 },
                vcStageMultipliers: { Beta: 0.5, 'Early Traction': 0.8, Commercial: 1.2, Mature: 1.5 }
            },
            fundraising: {
                ffTargetRaise: 2000000, ffExpectedDilution: 7.5,
                vcTargetRaiseMin: 10000000, vcTargetRaiseMax: 25000000, vcExpectedDilution: 15,
                useOfFunds: { productDev: 40, salesMarketing: 30, teamHiring: 20, operations: 10 }
            }
        }
    });

    const isReadOnly = companyData.length > 0;

    const register = (name, options) => {
        const { ref, ...rest } = formRegister(name, options);
        return { ...rest, inputRef: ref, ...(isReadOnly ? { InputProps: { readOnly: true } } : {}) };
    };

    const { fields: companyFields, append: appendCompany, remove: removeCompany } = useFieldArray({ control, name: 'companies' });
    const companies = watch('companies');
    const valuationParams = watch('valuationParams');
    const fundraising = watch('fundraising');


    // --- Helper Functions ---
    const addFounder = (companyIndex) => {
        const current = getValues(`companies.${companyIndex}.founders`) || [];
        setValue(`companies.${companyIndex}.founders`, [...current, { ...defaultFounder }]);
    };
    const removeFounder = (companyIndex, founderIndex) => {
        const current = getValues(`companies.${companyIndex}.founders`) || [];
        setValue(`companies.${companyIndex}.founders`, current.filter((_, i) => i !== founderIndex));
    };
    const addInvestor = (companyIndex) => {
        const current = getValues(`companies.${companyIndex}.existingInvestors`) || [];
        setValue(`companies.${companyIndex}.existingInvestors`, [...current, { ...defaultInvestor }]);
    };
    const removeInvestor = (companyIndex, investorIndex) => {
        const current = getValues(`companies.${companyIndex}.existingInvestors`) || [];
        setValue(`companies.${companyIndex}.existingInvestors`, current.filter((_, i) => i !== investorIndex));
    };
    const addProduct = (companyIndex) => {
        const current = getValues(`companies.${companyIndex}.products`) || [];
        setValue(`companies.${companyIndex}.products`, [...current, { ...defaultProduct }]);
    };
    const removeProduct = (companyIndex, productIndex) => {
        const current = getValues(`companies.${companyIndex}.products`) || [];
        setValue(`companies.${companyIndex}.products`, current.filter((_, i) => i !== productIndex));
    };
    const toggleExpanded = (companyIndex) => {
        const current = getValues(`companies.${companyIndex}.expanded`);
        setValue(`companies.${companyIndex}.expanded`, !current);
    };

    useEffect(() => {
        fetchDocumentData();
    },[])

    // Populate form fields when document data is loaded
    useEffect(() => {
        if (documentData && documentData.length > 0) {
            const mappedCompanies = documentData.map((company) => ({
                name: company.company_name || '',
                legalStructure: company.legal_structure || '',
                vision: company.vision_statement || '',
                platformStrategy: company.platform_strategy || '',
                foundingDate: company.founding_date || '',
                incorporationState: company.incorporation_state || '',
                currentStage: mapCompanyStage(company.current_stage),
                ipOwnership: company.ip_ownership_structure || '',
                founders: (company.founders || []).map(f => ({
                    name: f.name || '',
                    ownership: parsePercent(f.ownership_percent),
                })),
                existingInvestors: (company.existing_investors || []).map(i => ({
                    name: i.investor_name || '',
                    ownership: parsePercent(i.ownership_percent),
                    investmentDate: i.investment_date || '',
                    amount: parseCurrency(i.investment_amount),
                })),
                esopPool: parsePercent(company.esop_pool_percent),
                keyTeamMembers: company.key_team_members || '',
                exitStrategy: company.exit_strategy || '',
                milestone12Month: company.milestone_12_month || '',
                milestone18Month: company.milestone_18_month || '',
                expanded: true,
                products: (company.products || []).map(p => ({
                    name: p.product_name || '',
                    description: p.product_description || '',
                    category: p.product_category || '',
                    stage: mapProductStage(p.product_stage),
                    launchDate: p.launch_date || '',
                    currentARR: parseCurrency(p.current_arr),
                    currentMRR: parseCurrency(p.current_mrr),
                    payingCustomers: parseCustomerCount(p.paying_customers),
                    arpu: parseCurrency(p.arpu),
                    growthRate: parsePercent(p.growth_rate_percent),
                    year1ARR: parseCurrency(p.year1_arr),
                    year2ARR: parseCurrency(p.year2_arr),
                    year3ARR: parseCurrency(p.year3_arr),
                    tam: parseCurrency(p.tam),
                    sam: parseCurrency(p.sam),
                    revenueMultiple: parseCurrency(p.revenue_multiple) || 10,
                    riskFactor: parsePercent(p.risk_adjustment) || 1,
                    strategicValue: parseCurrency(p.strategic_value) || 0,
                })),
            }));

            setValue('companies', mappedCompanies);
        }
    }, [documentData, setValue]);

    const fetchDocumentData = async () => {
        try {
            setLoading(true);
            const resp = await axios.get(`/apiV1/advanced-ai-parsing/list/`)

            if(resp.status === 200){
                const sessionData = resp.data.data;
                setSessionID(sessionData.ai_session_id);
                if (sessionData && sessionData.parsed_data) {
                    const companies = sessionData.parsed_data.companies || [];
                    setCompanyData(companies);
                    const transformed = companies.map(company => ({
                        ...company,
                        founders: (company.founders || []).map(f => ({
                            name: f.founder_name || '',
                            ownership_percent: f.pecentage || '',
                        })),
                        existing_investors: (company.existing_investors || []).map(i => ({
                            investor_name: i.investor_name || '',
                            ownership_percent: i.ownership_percentage || '',
                            investment_date: i.data_of_joining || '',
                            investment_amount: i.amount || '',
                        })),
                    }));
                    setDocumentData(transformed);
                }
            }
        } catch (error) {
            console.error("Error fetching document data:", error);
        }finally{
            setLoading(false);
        }
    }

    const handleTopLevelDocumentUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                setLoading(true);
                setTopLevelDocument(file);

                const formData = new FormData();
                formData.append("doc", file);

                const resp = await axios.post("/apiV1/advanced-ai-parsing/parse/",
                    formData,
                    {
                        withCredentials: true,
                    });

                if (resp.status === 200 || resp.status === 201) {
                    setDocumentData(resp.data.data.parsed_data.companies || []);
                    setTopLevelDocument(null);
                }

            } catch (error) {
                console.log("Error uploading document:", error);
            }finally{
                setLoading(false);
            }

        }
    };
    
 

    const mapApiProduct = (p) => ({
        name: p.product_name || '',
        description: p.product_description || '',
        category: p.product_category || '',
        stage: mapProductStage(p.product_stage),
        launchDate: p.launch_date || '',
        currentARR: parseCurrency(p.current_arr),
        currentMRR: parseCurrency(p.current_mrr),
        payingCustomers: parseCustomerCount(p.paying_customers),
        arpu: parseCurrency(p.arpu),
        growthRate: parsePercent(p.growth_rate_percent),
        year1ARR: parseCurrency(p.year1_arr),
        year2ARR: parseCurrency(p.year2_arr),
        year3ARR: parseCurrency(p.year3_arr),
        tam: parseCurrency(p.tam),
        sam: parseCurrency(p.sam),
        revenueMultiple: parseCurrency(p.revenue_multiple) || 10,
        riskFactor: parsePercent(p.risk_adjustment) || 1,
        strategicValue: parseCurrency(p.strategic_value) || 0,
    });

    const handleProductDocumentUpload = async (event, companyIndex) => {
        const file = event.target.files[0];
        if (!file) return;
        try {
            setProductUploadingIdx(companyIndex);
            const formData = new FormData();
            formData.append("doc", file);
            const resp = await axios.post("/apiV1/advanced-ai-parsing/product-parse/", formData, { withCredentials: true });
            if (resp.status === 200 || resp.status === 201) {
                const products = resp.data?.data?.parsed_data?.products || [];
                if (products.length > 0) {
                    const currentProducts = getValues(`companies.${companyIndex}.products`) || [];
                    const newProducts = products.map(p => mapApiProduct(p));
                    setValue(`companies.${companyIndex}.products`, [...currentProducts, ...newProducts]);
                }
            }
        } catch (error) {
            console.log("Error uploading product document:", error);
        } finally {
            setProductUploadingIdx(null);
            event.target.value = '';
        }
    };

    console.log("Document Data:", documentData);

    // --- Calculations ---
    const calculateProductValuation = (product, isFnF = true) => {
        const baseRevenue = isFnF ? (product.currentARR || product.year1ARR * 0.3) : product.year2ARR;
        const multiple = isFnF ? valuationParams.ffRevenueMultiple : product.revenueMultiple;
        const stageMultiplier = isFnF
            ? valuationParams.ffStageMultipliers[product.stage]
            : valuationParams.vcStageMultipliers[product.stage];
        return baseRevenue * multiple * (stageMultiplier || 0);
    };
    const calculateCompanyValuation = (company, isFnF = true) => {
        const productSum = (company.products || []).reduce((sum, p) => sum + calculateProductValuation(p, isFnF), 0);
        return productSum * (1 + valuationParams.platformPremium / 100) * (1 + valuationParams.executionPremium / 100);
    };
    const calculateTotalValuation = (isFnF = true) =>
        (companies || []).reduce((sum, c) => sum + calculateCompanyValuation(c, isFnF), 0);
    const getAllProducts = () =>
        (companies || []).flatMap(c => (c.products || []).map(p => ({ ...p, companyName: c.name })));

    const ffTotal = calculateTotalValuation(true);
    const vcTotal = calculateTotalValuation(false);
    const actualDiscount = vcTotal > 0 ? ((vcTotal - ffTotal) / vcTotal * 100).toFixed(1) : '0.0';
    const useOfFundsTotal = Object.values(fundraising.useOfFunds || {}).reduce((a, b) => a + b, 0);

    const getTotalFounderOwnership = (company) =>
        (company.founders || []).reduce((sum, f) => sum + (parseFloat(f.ownership) || 0), 0);
    const getTotalInvestorOwnership = (company) =>
        (company.existingInvestors || []).reduce((sum, i) => sum + (parseFloat(i.ownership) || 0), 0);


    const valuationSuggestions = async() =>{
        try {
            setLoading(true);
            const payload = {
                ai_session_id: sessionID, 
                language: "English"
            }

            const resp  = await axios.post('apiV1/advanced-ai-parsing/valuation-suggestions/',payload)

            if(resp.status === 200){
                const data = resp.data.data.parsed_data;
                setValue('valuationParams.ffRevenueMultiple', parseFloat(data.ff_revenue_multiple) );
                setValue('valuationParams.vcRevenueMultiple', parseFloat(data.vc_revenue_multiple) );
                setValue('valuationParams.platformPremium', parseFloat(data.platform_premium) );
                setValue('valuationParams.executionPremium', parseFloat(data.execution_premium) );

                Object.keys(data.ff_stage_multipliers || {}).forEach(stage => {
                    setValue(`valuationParams.ffStageMultipliers.${stage}`, parseFloat(data.ff_stage_multipliers[stage]) );
                });
                
                Object.keys(data.vc_stage_multipliers || {}).forEach(stage => {
                    setValue(`valuationParams.vcStageMultipliers.${stage}`, parseFloat(data.vc_stage_multipliers[stage]) );
                });
            }

        } catch (error) {
            console.log("Error fetching valuation suggestions:", error);
        }finally{
            setLoading(false);
        }
    }

    const fundraisingSuggestions = async() =>{
        try {
            setLoading(true);
            const payload = {
                ai_session_id: sessionID, 
                language: "English"
            }

            const resp  = await axios.post('apiV1/advanced-ai-parsing/fundraising-suggestions/',payload)

            if(resp.status === 200 || resp.status === 201){
                console.log("Fundraising Suggestions:", resp.data.data);
                const parsed = resp.data.data.parsed_data;
                if (parsed) {
                    setValue('fundraising.ffTargetRaise', parseCurrency(parsed.ff_target_raise));
                    setValue('fundraising.ffExpectedDilution', parsePercent(parsed.ff_expected_dilution));
                    setValue('fundraising.vcTargetRaiseMin', parseCurrency(parsed.vc_target_raise_min));
                    setValue('fundraising.vcTargetRaiseMax', parseCurrency(parsed.vc_target_raise_max));
                    setValue('fundraising.vcExpectedDilution', parsePercent(parsed.vc_expected_dilution));
                    if (parsed.use_of_funds) {
                        setValue('fundraising.useOfFunds.productDev', parsePercent(parsed.use_of_funds.product_development));
                        setValue('fundraising.useOfFunds.salesMarketing', parsePercent(parsed.use_of_funds.sales_marketing));
                        setValue('fundraising.useOfFunds.teamHiring', parsePercent(parsed.use_of_funds.team_hiring));
                        setValue('fundraising.useOfFunds.operations', parsePercent(parsed.use_of_funds.operations));
                    }
                }
                setActiveStep(2);
            }

        } catch (error) {
            console.log("Error fetching valuation suggestions:", error);
        }finally{
            setLoading(false);
        }
    }



    // --- Step-specific Save Handlers ---
    const handleSaveStep1 = async () => {
        // Expand all collapsed companies so their fields mount and get registered for validation
        const currentCompanies = getValues('companies') || [];
        let hasCollapsed = false;
        currentCompanies.forEach((_, idx) => {
            if (!getValues(`companies.${idx}.expanded`)) {
                setValue(`companies.${idx}.expanded`, true);
                hasCollapsed = true;
            }
        });
        if (hasCollapsed) {
            // Wait for re-render so fields mount and register with react-hook-form
            await new Promise(resolve => setTimeout(resolve, 150));
        }

        // Now trigger validation and submission
        await handleSubmit(async (data) => {
            try {
                setLoading(true);
                const payload = {
                    language: "English",
                    parsed_data: {
                        companies: (data.companies || []).map((company) => ({
                            company_name: company.name || "",
                            legal_structure: company.legalStructure || "",
                            vision_statement: company.vision || "",
                            platform_strategy: company.platformStrategy || "",
                            founding_date: company.foundingDate || "",
                            incorporation_state: company.incorporationState || "",
                            current_stage: company.currentStage || "",
                            ip_ownership_structure: company.ipOwnership || "",
                            founders: (company.founders || []).map((f) => ({
                                founder_name: f.name || "",
                                pecentage: String(f.ownership || ""),
                            })),
                            existing_investors: (company.existingInvestors || []).map((i) => ({
                                investor_name: i.name || "",
                                ownership_percentage: String(i.ownership || ""),
                                data_of_joining: i.investmentDate || "",
                                amount: String(i.amount || ""),
                            })),
                            esop_pool_percent: String(company.esopPool || ""),
                            key_team_members: company.keyTeamMembers || "",
                            exit_strategy: company.exitStrategy || "",
                            milestone_12_month: company.milestone12Month || "",
                            milestone_18_month: company.milestone18Month || "",
                            products: (company.products || []).map((p) => ({
                                product_name: p.name || "",
                                product_description: p.description || "",
                                product_category: p.category || "",
                                product_stage: p.stage || "",
                                launch_date: p.launchDate || "",
                                current_arr: String(p.currentARR || ""),
                                current_mrr: String(p.currentMRR || ""),
                                paying_customers: String(p.payingCustomers || ""),
                                arpu: String(p.arpu || ""),
                                growth_rate_percent: String(p.growthRate || ""),
                                year1_arr: String(p.year1ARR || ""),
                                year2_arr: String(p.year2ARR || ""),
                                year3_arr: String(p.year3ARR || ""),
                                tam: String(p.tam || ""),
                                sam: String(p.sam || ""),
                                revenue_multiple: String(p.revenueMultiple || ""),
                                risk_adjustment: String(p.riskFactor || ""),
                                strategic_value: String(p.strategicValue || ""),
                            })),
                        })),
                    },
                };

                const resp = await axios.post(
                    "/apiV1/advanced-ai-parsing/save/",
                    payload,
                    { withCredentials: true }
                );

                if (resp.status === 200 || resp.status === 201) {
                    setActiveStep(1);
                    valuationSuggestions();
                }
            } catch (error) {
                console.log("Error saving company data:", error);
                setLoading(false);
            } 
        })();
    };
    const handleSaveStep2 = async () => {
        try {
            setLoading(true);
            const params = getValues('valuationParams');
            const payload = {
                parsed_data: {
                    ff_revenue_multiple: `${params.ffRevenueMultiple}`,
                    vc_revenue_multiple: `${params.vcRevenueMultiple}`,
                    ff_stage_multipliers: {
                        Beta: `${params.ffStageMultipliers.Beta}`,
                        'Early Traction': `${params.ffStageMultipliers['Early Traction']}`,
                        Commercial: `${params.ffStageMultipliers.Commercial}`,
                        Mature: `${params.ffStageMultipliers.Mature}`,
                    },
                    vc_stage_multipliers: {
                        Beta: `${params.vcStageMultipliers.Beta}`,
                        'Early Traction': `${params.vcStageMultipliers['Early Traction']}`,
                        Commercial: `${params.vcStageMultipliers.Commercial}`,
                        Mature: `${params.vcStageMultipliers.Mature}`,
                    },
                    platform_premium: `${params.platformPremium}%`,
                    execution_premium: `${params.executionPremium}%`,
                }
            };
            const resp =await axios.put('/apiV1/advanced-ai-parsing/valuation-suggestions/', payload, { withCredentials: true });

            if(resp.status === 200 || resp.status === 201){
                fundraisingSuggestions()
            }
        } catch (error) {
            console.log("Error saving valuation settings:", error);
            setLoading(false);
        } 
    };
    const handleSaveStep3 = () => {
        // Future: API call to save Fundraising data
        setActiveStep(3);
    };
    // const handleFinalSubmit = () => {
    //     // Future: Final submission API call
    // };

    return (
       <div ref={scrollContainerRef}>
       {loading &&
          <Box
                       sx={{
                         display: "flex",
                         justifyContent: "center",
                         alignItems: "center",
                         height: "100vh",
                         width: "100vw",
                         position: "fixed",
                         top: 0,
                         left: 0,
                         backgroundColor: "rgba(255, 255, 255, 0.8)",
                         zIndex: 9999,
                       }}
                     >
                       <BeatLoader color=" #07659c" size={20} />
                     </Box>}
        <PageContainer title="SmartInvestorDeck | Product Valuation">
            <Box >
                {/* Header */}
                <div style={{ marginBottom: '24px', textAlign: 'center' }}>
                    <Typography sx={{
                        fontSize: { xs: '1.5rem', md: '1.8rem' }, fontWeight: 800,
                        background: 'linear-gradient(180deg, var(--primary-black), var(--primary-red))',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>Product Valuation Generator</Typography>
                    <Typography sx={{ fontSize: '0.9rem', color: '#6c757d' }}>Generate F&F and VC valuation documents for multi-product companies</Typography>
                </div>

                {/* Stepper */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        width: '100%',
                        mb: 4,
                        border: '1px solid #ddeef8',
                        backgroundColor: '#f8fafd',
                        borderRadius: '12px',
                        overflowX: 'auto',
                    }}
                >
                    {steps.map((step, index) => {
                        const isCompleted = index < activeStep;
                        const isActive = index === activeStep;
                        const isFuture = index > activeStep;
                        return (
                            <Box
                                key={step.label}
                                onClick={() => { if (isCompleted) setActiveStep(index); }}
                                sx={{
                                    flex: 1,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                                    py: 1.7,
                                    cursor: isCompleted ? 'pointer' : 'default',
                                    borderBottom: '3px solid transparent',
                                    transition: 'all 0.25s ease',
                                    ...(isActive && {
                                        borderBottomColor: '#076599',
                                    }),
                                    ...(isCompleted && {
                                        borderBottomColor: '#66a321',
                                    }),
                                    '&:hover': isCompleted ? {
                                        backgroundColor: 'rgba(102,163,33,0.04)',
                                    } : {},
                                }}
                            >
                                {/* Number circle */}
                                <Box sx={{
                                    width: 26, height: 26, borderRadius: '50%',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0,
                                    fontSize: '0.75rem', fontWeight: 700,
                                    transition: 'all 0.25s ease',
                                    ...(isCompleted && {
                                        backgroundColor: '#66a321', color: '#fff',
                                    }),
                                    ...(isActive && {
                                        backgroundColor: '#076599', color: '#fff',
                                    }),
                                    ...(isFuture && {
                                        backgroundColor: '#e0e4e8', color: '#9ca3af',
                                    }),
                                }}>
                                    {isCompleted ? <IconCheck size={14} stroke={3} /> : index + 1}
                                </Box>

                                {/* Icon */}
                                <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                                    {React.cloneElement(step.icon, {
                                        size: 17,
                                        color: isCompleted ? '#66a321' : isActive ? '#076599' : '#9ca3af',
                                        stroke: isActive ? 2 : 1.5,
                                    })}
                                </Box>

                                {/* Label */}
                                <Typography sx={{
                                    fontSize: { xs: '0.68rem', sm: '0.78rem', md: '0.85rem' },
                                    fontWeight: isActive ? 700 : 600,
                                    color: isCompleted ? '#66a321' : isActive ? '#076599' : '#9ca3af',
                                    whiteSpace: 'nowrap',
                                    transition: 'color 0.25s ease',
                                }}>
                                    {step.label}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>

                {/* Step Content */}
                <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: '12px', border: '1px solid #e9ecef', mb: 3 }} elevation={0}>

                    {/* ============================
                        STEP 1: Companies & Products
                        ============================ */}
                    {activeStep === 0 && (
                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                                <Box>
                                    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#2b2c2a' }}>Companies & Products</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#6c757d', mt: 0.5 }}>Add companies and their respective products or services</Typography>
                                </Box>
                                <Box>
                                    <input type="file" ref={topLevelFileInputRef} accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.png,.jpg,.jpeg" onChange={(e) => { handleTopLevelDocumentUpload(e); setAddCompanyModalOpen(false); }} style={{ display: 'none' }} />
                                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={() => setAddCompanyModalOpen(true)}
                                        disabled={isReadOnly}
                                        sx={{
                                            background: isReadOnly ? undefined : 'linear-gradient(135deg, #076599 0%, #054d75 100%)',
                                            '&:hover': { background: isReadOnly ? undefined : 'linear-gradient(135deg, #065280 0%, #043d5e 100%)' },
                                            textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3, py: 1,
                                            boxShadow: isReadOnly ? 'none' : '0 4px 14px rgba(7,101,153,0.3)',
                                        }}>
                                        Add Company
                                    </Button>
                                </Box>
                            </Box>

                            {topLevelDocument && (
                                <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}
                                    action={<IconButton size="small" onClick={() => setTopLevelDocument(null)}><IconX size={16} /></IconButton>}>
                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>Document Uploaded: {topLevelDocument.name}</Typography>
                                </Alert>
                            )}

                            {companyFields.map((field, companyIndex) => {
                                const company = companies?.[companyIndex] || {};
                                const companyPath = `companies.${companyIndex}`;
                                return (
                                    <Paper key={field.id} sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden', border: '1px solid #e0e4e8' }} elevation={0}>
                                        {/* Company Header */}
                                        <Box className="pv-company-header" onClick={() => toggleExpanded(companyIndex)}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <IconChevronDown size={22} color="#fff" style={{ transform: company.expanded ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.3s' }} />
                                                <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff' }}>
                                                   <span style={{color:"#eef5fc"}}> Company {companyIndex + 1}:</span> {company.name || 'New Company'}
                                                </Typography>
                                            </Box>
                                            {companyFields.length > 1 && (
                                                <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); removeCompany(companyIndex); }}
                                                    disabled={isReadOnly}
                                                    sx={{ backgroundColor: '#c53131', '&:hover': { backgroundColor: '#a52828' }, textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.8rem' }}>
                                                    Remove
                                                </Button>
                                            )}
                                        </Box>

                                        {/* Company Body */}
                                        {company.expanded && (
                                            <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f0f2f5' }}>
                                                {/* Basic Information */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header">
                                                        <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #076599 0%, #0a7bb8 100%)' }}>
                                                            <IconInfoCircle size={16} color="#fff" />
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Basic Information</Typography>
                                                            <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Company details, structure & strategy</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography className="pv-label">Company Name *</Typography>
                                                                <TextField {...register(`${companyPath}.name`, { required: 'Company name is required' })} fullWidth placeholder="e.g., VitelGlobal Group" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.name} helperText={errors.companies?.[companyIndex]?.name?.message} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography className="pv-label">Legal Structure *</Typography>
                                                                <TextField {...register(`${companyPath}.legalStructure`, { required: 'Legal structure is required' })} fullWidth placeholder="e.g., Delaware C-Corporation" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.legalStructure} helperText={errors.companies?.[companyIndex]?.legalStructure?.message} />
                                                            </Grid>
                                                            <Grid item xs={12} md={4}>
                                                                <Typography className="pv-label">Founding Date *</Typography>
                                                                <TextField {...register(`${companyPath}.foundingDate`, { required: 'Founding date is required' })} fullWidth placeholder="e.g., 2020" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.foundingDate} helperText={errors.companies?.[companyIndex]?.foundingDate?.message} />
                                                            </Grid>
                                                            <Grid item xs={12} md={4}>
                                                                <Typography className="pv-label">Incorporation State *</Typography>
                                                                <TextField {...register(`${companyPath}.incorporationState`, { required: 'Incorporation state is required' })} fullWidth placeholder="e.g., Delaware" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.incorporationState} helperText={errors.companies?.[companyIndex]?.incorporationState?.message} />
                                                            </Grid>
                                                            <Grid item xs={12} md={4}>
                                                                <Typography className="pv-label">Current Stage *</Typography>
                                                                <Controller name={`${companyPath}.currentStage`} control={control}
                                                                    rules={{ required: 'Current stage is required' }}
                                                                    render={({ field }) => (
                                                                        <FormControl fullWidth size="small" error={!!errors.companies?.[companyIndex]?.currentStage}>
                                                                            <Select {...field} readOnly={isReadOnly} displayEmpty sx={{ borderRadius: '8px', fontSize: '0.875rem', height: '40px' }}>
                                                                                <MenuItem value="">--Select Stage--</MenuItem>
                                                                                {companyStageOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                                                            </Select>
                                                                            {errors.companies?.[companyIndex]?.currentStage && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.companies[companyIndex].currentStage.message}</Typography>}
                                                                        </FormControl>
                                                                    )} />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography className="pv-label">Vision Statement *</Typography>
                                                                <TextField {...register(`${companyPath}.vision`, { required: 'Vision statement is required' })} fullWidth multiline rows={2} placeholder="Describe the company's vision and mission" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.vision} helperText={errors.companies?.[companyIndex]?.vision?.message} />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography className="pv-label">Platform Strategy *</Typography>
                                                                <TextField {...register(`${companyPath}.platformStrategy`, { required: 'Platform strategy is required' })} fullWidth multiline rows={2} placeholder="Explain the platform approach and synergies" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.platformStrategy} helperText={errors.companies?.[companyIndex]?.platformStrategy?.message} />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Paper>

                                                {/* IP & Ownership */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header">
                                                        <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #0d7377 0%, #0f9199 100%)' }}>
                                                            <IconShieldCheck size={16} color="#fff" />
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>IP & Ownership</Typography>
                                                            <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Intellectual property structure</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        <Typography className="pv-label">IP Ownership Structure *</Typography>
                                                        <TextField {...register(`${companyPath}.ipOwnership`, { required: 'IP ownership structure is required' })} fullWidth multiline rows={2} placeholder="Describe IP ownership, assignments, and work-for-hire agreements" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.ipOwnership} helperText={errors.companies?.[companyIndex]?.ipOwnership?.message} />
                                                    </Box>
                                                </Paper>

                                                {/* Founders */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header" sx={{ justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #66a321 0%, #7bb830 100%)' }}>
                                                                <IconUsers size={16} color="#fff" />
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Founder Ownership</Typography>
                                                                <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Founding team and equity split</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Button size="small" startIcon={<IconPlus size={14} />} onClick={() => addFounder(companyIndex)}
                                                            disabled={isReadOnly}
                                                            sx={{ backgroundColor: '#66a321', color: isReadOnly ? undefined : '#fff', px: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.75rem', '&:hover': { backgroundColor: isReadOnly ? undefined : '#558a1b' } }}>
                                                            Add Founder
                                                        </Button>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        {(!company.founders || company.founders.length === 0) ? (
                                                            <Box sx={{ textAlign: 'center', py: 3, backgroundColor: '#f8faf5', borderRadius: '10px', border: '1px dashed #c8e6c9' }}>
                                                                <IconUsers size={28} color="#a5d6a7" style={{ marginBottom: 8 }} />
                                                                <Typography sx={{ fontSize: '0.85rem', color: '#6c757d' }}>No founders added. Click "Add Founder" to start.</Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box>
                                                                {company.founders.map((founder, fIdx) => {
                                                                    const founderPath = `${companyPath}.founders.${fIdx}`;
                                                                    return (
                                                                        <Box key={fIdx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1.5, backgroundColor: '#f8faf5', borderRadius: '8px', border: '1px solid #e8f5e9' }}>
                                                                            <TextField {...register(`${founderPath}.name`)} fullWidth placeholder="Founder Name" size="small" className="pv-input" sx={{ flex: 1 }} />
                                                                            <TextField {...register(`${founderPath}.ownership`, { valueAsNumber: true })} type="number" placeholder="0" size="small" className="pv-input" sx={{ width: '100px' }} />
                                                                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>%</Typography>
                                                                            {company.founders.length > 1 && (
                                                                                <IconButton size="small" onClick={() => removeFounder(companyIndex, fIdx)} disabled={isReadOnly} sx={{ color: isReadOnly ? undefined : '#c53131' }}>
                                                                                    <IconX size={16} />
                                                                                </IconButton>
                                                                            )}
                                                                        </Box>
                                                                    );
                                                                })}
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #e8f5e9', mt: 1 }}>
                                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                                                        Total: <span style={{ color: getTotalFounderOwnership(company) > 100 ? '#c53131' : '#2b2c2a' }}>
                                                                            {getTotalFounderOwnership(company).toFixed(1)}%
                                                                        </span>
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Paper>

                                                {/* Investors */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header" sx={{ justifyContent: 'space-between' }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #5c6bc0 0%, #7986cb 100%)' }}>
                                                                <IconCurrencyDollar size={16} color="#fff" />
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Existing Investors</Typography>
                                                                <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Current investor details and stakes</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Button size="small" startIcon={<IconPlus size={14} />} onClick={() => addInvestor(companyIndex)}
                                                            disabled={isReadOnly}
                                                            sx={{ backgroundColor: '#5c6bc0', color: isReadOnly ? undefined : '#fff', textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 1.5, fontSize: '0.75rem', '&:hover': { backgroundColor: isReadOnly ? undefined : '#4a5ab0' } }}>
                                                            Add Investor
                                                        </Button>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        {(!company.existingInvestors || company.existingInvestors.length === 0) ? (
                                                            <Box sx={{ textAlign: 'center', py: 3, backgroundColor: '#f3f4fb', borderRadius: '10px', border: '1px dashed #c5cae9' }}>
                                                                <IconCurrencyDollar size={28} color="#c5cae9" style={{ marginBottom: 8 }} />
                                                                <Typography sx={{ fontSize: '0.85rem', color: '#6c757d' }}>No investors added. Click "Add Investor" to start.</Typography>
                                                            </Box>
                                                        ) : (
                                                            <Box>
                                                                {company.existingInvestors.map((investor, iIdx) => {
                                                                    const investorPath = `${companyPath}.existingInvestors.${iIdx}`;
                                                                    return (
                                                                        <Box key={iIdx} sx={{ p: 1.5, mb: 1, backgroundColor: '#f8f8fd', borderRadius: '8px', border: '1px solid #e8eaf6' }}>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                                <TextField {...register(`${investorPath}.name`)} fullWidth placeholder="Investor Name" size="small" className="pv-input" sx={{ flex: 1 }} />
                                                                                <IconButton size="small" onClick={() => removeInvestor(companyIndex, iIdx)} disabled={isReadOnly} sx={{ color: isReadOnly ? undefined : '#c53131' }}>
                                                                                    <IconX size={16} />
                                                                                </IconButton>
                                                                            </Box>
                                                                            <Grid container spacing={1}>
                                                                                <Grid item xs={4}>
                                                                                    <TextField {...register(`${investorPath}.ownership`, { valueAsNumber: true })} type="number" placeholder="Ownership %" size="small" fullWidth className="pv-input" />
                                                                                </Grid>
                                                                                <Grid item xs={4}>
                                                                                    <TextField {...register(`${investorPath}.investmentDate`)} placeholder="2023-05" size="small" fullWidth className="pv-input" />
                                                                                </Grid>
                                                                                <Grid item xs={4}>
                                                                                    <TextField {...register(`${investorPath}.amount`, { valueAsNumber: true })} type="number" placeholder="Amount ($)" size="small" fullWidth className="pv-input" />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Box>
                                                                    );
                                                                })}
                                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1, borderTop: '1px solid #e8eaf6', mt: 1 }}>
                                                                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                                                        Total: <span style={{ color: getTotalInvestorOwnership(company) > 100 ? '#c53131' : '#2b2c2a' }}>
                                                                            {getTotalInvestorOwnership(company).toFixed(1)}%
                                                                        </span>
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Paper>

                                                {/* ESOP */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header">
                                                        <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #e65100 0%, #f57c00 100%)' }}>
                                                            <IconPercentage size={16} color="#fff" />
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>ESOP Pool</Typography>
                                                            <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Employee stock option allocation</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={4}>
                                                                <Typography className="pv-label">ESOP Pool (%) *</Typography>
                                                                <TextField {...register(`${companyPath}.esopPool`, { valueAsNumber: true, required: 'ESOP pool is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 10" error={!!errors.companies?.[companyIndex]?.esopPool} helperText={errors.companies?.[companyIndex]?.esopPool?.message} />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Paper>

                                                {/* Team & Strategy */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header">
                                                        <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #1a237e 0%, #303f9f 100%)' }}>
                                                            <IconTarget size={16} color="#fff" />
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Team & Strategy</Typography>
                                                            <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Key members and exit planning</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12}>
                                                                <Typography className="pv-label">Key Team Members *</Typography>
                                                                <TextField {...register(`${companyPath}.keyTeamMembers`, { required: 'Key team members is required' })} fullWidth multiline rows={2} placeholder="List key executives and their roles" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.keyTeamMembers} helperText={errors.companies?.[companyIndex]?.keyTeamMembers?.message} />
                                                            </Grid>
                                                            <Grid item xs={12}>
                                                                <Typography className="pv-label">Exit Strategy *</Typography>
                                                                <TextField {...register(`${companyPath}.exitStrategy`, { required: 'Exit strategy is required' })} fullWidth multiline rows={2} placeholder="Describe potential exit paths" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.exitStrategy} helperText={errors.companies?.[companyIndex]?.exitStrategy?.message} />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Paper>

                                                {/* Milestones */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header">
                                                        <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #f57f17 0%, #fbc02d 100%)' }}>
                                                            <IconFlag size={16} color="#fff" />
                                                        </Box>
                                                        <Box>
                                                            <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Milestones</Typography>
                                                            <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Key goals and growth targets</Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">
                                                        <Grid container spacing={2}>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography className="pv-label">12-Month Milestones *</Typography>
                                                                <TextField {...register(`${companyPath}.milestone12Month`, { required: '12-month milestones is required' })} fullWidth multiline rows={2} placeholder="Key goals for next 12 months" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.milestone12Month} helperText={errors.companies?.[companyIndex]?.milestone12Month?.message} />
                                                            </Grid>
                                                            <Grid item xs={12} md={6}>
                                                                <Typography className="pv-label">18-Month Milestones *</Typography>
                                                                <TextField {...register(`${companyPath}.milestone18Month`, { required: '18-month milestones is required' })} fullWidth multiline rows={2} placeholder="Key goals for next 18 months" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.milestone18Month} helperText={errors.companies?.[companyIndex]?.milestone18Month?.message} />
                                                            </Grid>
                                                        </Grid>
                                                    </Box>
                                                </Paper>

                                                {/* Products Section */}
                                                <Paper className="pv-section-card" elevation={0}>
                                                    <Box className="pv-section-card-header" sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Box className="pv-section-card-icon" sx={{ background: 'linear-gradient(135deg, #c53131 0%, #e04545 100%)' }}>
                                                                <IconPackage size={16} color="#fff" />
                                                            </Box>
                                                            <Box>
                                                                <Typography sx={{ fontSize: '0.92rem', fontWeight: 700, color: '#2b2c2a' }}>Products / Services</Typography>
                                                                <Typography sx={{ fontSize: '0.72rem', color: '#6c757d', mt: -0.2 }}>Product details and financial metrics</Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Tooltip title="Upload a product document to auto-fill product details" arrow>
                                                                <label style={{ cursor: isReadOnly ? 'default' : 'pointer', margin: 0 }}>
                                                                    <input
                                                                        type="file"
                                                                        accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.png,.jpg,.jpeg"
                                                                        style={{ display: 'none' }}
                                                                        disabled={isReadOnly}
                                                                        onChange={(e) => handleProductDocumentUpload(e, companyIndex)}
                                                                    />
                                                                    <Button
                                                                        component="span"
                                                                        size="small"
                                                                        startIcon={productUploadingIdx === companyIndex ? <BeatLoader size={6} color="#fff" /> : <IconUpload size={14} />}
                                                                        disabled={isReadOnly || productUploadingIdx === companyIndex}
                                                                        sx={{ backgroundColor: '#076599', color: isReadOnly ? undefined : '#fff', px: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: '8px', fontSize: '0.8rem', '&:hover': { backgroundColor: isReadOnly ? undefined : '#065280' } }}
                                                                    >
                                                                        {productUploadingIdx === companyIndex ? 'Uploading...' : 'Upload Document'}
                                                                    </Button>
                                                                </label>
                                                            </Tooltip>
                                                            <Button size="small" startIcon={<IconPlus size={14} />} onClick={() => addProduct(companyIndex)}
                                                                disabled={isReadOnly}
                                                                sx={{ backgroundColor: '#66a321', color: isReadOnly ? undefined : '#fff', px: 1.5, textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { backgroundColor: isReadOnly ? undefined : '#558a1b' } }}>
                                                                Add Product
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                    <Box className="pv-section-card-body">

                                                    {(!company.products || company.products.length === 0) ? (
                                                        <Box sx={{ textAlign: 'center', py: 4, backgroundColor: '#fef5f5', borderRadius: '10px', border: '2px dashed #ffcdd2' }}>
                                                            <IconPackage size={28} color="#ef9a9a" style={{ marginBottom: 8 }} />
                                                            <Typography sx={{ color: '#6c757d' }}>No products added yet. Click "Add Product" to get started.</Typography>
                                                        </Box>
                                                    ) : (
                                                        company.products.map((product, pIdx) => {
                                                            const productPath = `${companyPath}.products.${pIdx}`;
                                                            return (
                                                                <Paper key={pIdx} className="pv-product-card" elevation={0}>
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#2b2c2a' }}>
                                                                            Product {pIdx + 1}: {product?.name || 'New Product'}
                                                                        </Typography>
                                                                        <Button size="small" onClick={() => removeProduct(companyIndex, pIdx)}
                                                                            disabled={isReadOnly}
                                                                            sx={{ color:  '#fff', textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}>
                                                                            Remove
                                                                        </Button>
                                                                    </Box>

                                                                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Product Name *</Typography>
                                                                            <TextField {...register(`${productPath}.name`, { required: 'Product name is required' })} fullWidth placeholder="e.g., DigiSuite" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.name} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.name?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Category *</Typography>
                                                                            <TextField {...register(`${productPath}.category`, { required: 'Category is required' })} fullWidth placeholder="e.g., MarTech" size="small" className="pv-input" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.category} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.category?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Stage *</Typography>
                                                                            <Controller name={`${productPath}.stage`} control={control}
                                                                                rules={{ required: 'Stage is required' }}
                                                                                render={({ field }) => (
                                                                                    <FormControl fullWidth size="small" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.stage}>
                                                                                        <Select {...field} readOnly={isReadOnly} displayEmpty sx={{ borderRadius: '8px', fontSize: '0.875rem', height: '40px' }}>
                                                                                            <MenuItem value="">--Select Stage--</MenuItem>
                                                                                            {stageOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                                                                                        </Select>
                                                                                        {errors.companies?.[companyIndex]?.products?.[pIdx]?.stage && <Typography sx={{ color: '#d32f2f', fontSize: '0.75rem', mt: 0.5, ml: 1.5 }}>{errors.companies[companyIndex].products[pIdx].stage.message}</Typography>}
                                                                                    </FormControl>
                                                                                )} />
                                                                        </Grid>
                                                                    </Grid>

                                                                    <Box sx={{ mb: 1.5 }}>
                                                                        <Typography className="pv-small-label">Product Description *</Typography>
                                                                        <TextField {...register(`${productPath}.description`, { required: 'Product description is required' })} fullWidth multiline rows={2} placeholder="Describe the product, features, and value proposition" className="pv-textarea" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.description} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.description?.message} />
                                                                    </Box>

                                                                    {/* Financial Metrics */}
                                                                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                                                        <Grid item xs={6} md={3}>
                                                                            <Typography className="pv-small-label">Current ARR ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.currentARR`, { valueAsNumber: true, required: 'Current ARR is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 500000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.currentARR} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.currentARR?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={6} md={3}>
                                                                            <Typography className="pv-small-label">Customers *</Typography>
                                                                            <TextField {...register(`${productPath}.payingCustomers`, { valueAsNumber: true, required: 'Customers is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 50" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.payingCustomers} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.payingCustomers?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={6} md={3}>
                                                                            <Typography className="pv-small-label">ARPU ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.arpu`, { valueAsNumber: true, required: 'ARPU is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 1000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.arpu} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.arpu?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={6} md={3}>
                                                                            <Typography className="pv-small-label">Growth (%) *</Typography>
                                                                            <TextField {...register(`${productPath}.growthRate`, { valueAsNumber: true, required: 'Growth rate is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 25" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.growthRate} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.growthRate?.message} />
                                                                        </Grid>
                                                                    </Grid>

                                                                    <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Year 1 ARR ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.year1ARR`, { valueAsNumber: true, required: 'Year 1 ARR is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 600000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.year1ARR} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.year1ARR?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Year 2 ARR ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.year2ARR`, { valueAsNumber: true, required: 'Year 2 ARR is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 1200000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.year2ARR} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.year2ARR?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Year 3 ARR ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.year3ARR`, { valueAsNumber: true, required: 'Year 3 ARR is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 2500000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.year3ARR} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.year3ARR?.message} />
                                                                        </Grid>
                                                                    </Grid>

                                                                    <Grid container spacing={1.5}>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">TAM ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.tam`, { valueAsNumber: true, required: 'TAM is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 5000000000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.tam} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.tam?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">SAM ($) *</Typography>
                                                                            <TextField {...register(`${productPath}.sam`, { valueAsNumber: true, required: 'SAM is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 500000000" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.sam} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.sam?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Revenue Multiple *</Typography>
                                                                            <TextField {...register(`${productPath}.revenueMultiple`, { valueAsNumber: true, required: 'Revenue multiple is required' })} type="number" fullWidth size="small" className="pv-input" inputProps={{ step: 0.5 }} placeholder="e.g., 10" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.revenueMultiple} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.revenueMultiple?.message} />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Risk Adjustment *</Typography>
                                                                            <TextField {...register(`${productPath}.riskFactor`, { valueAsNumber: true, required: 'Risk adjustment is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 1" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.riskFactor} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.riskFactor?.message} />
                                                                        </Grid>
                                                                         <Grid item xs={12} md={4}>
                                                                            <Typography className="pv-small-label">Strategic Value *</Typography>
                                                                            <TextField {...register(`${productPath}.strategicValue`, { valueAsNumber: true, required: 'Strategic value is required' })} type="number" fullWidth size="small" className="pv-input" placeholder="e.g., 0" error={!!errors.companies?.[companyIndex]?.products?.[pIdx]?.strategicValue} helperText={errors.companies?.[companyIndex]?.products?.[pIdx]?.strategicValue?.message} />
                                                                        </Grid>
                                                                    </Grid>

                                                                    
                                                                </Paper>
                                                            );
                                                        })
                                                    )}
                                                    {company.products && company.products.length > 1 && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                                            <Button size="x-small" startIcon={<IconPlus size={14} />} onClick={() => addProduct(companyIndex)}
                                                                disabled={isReadOnly}
                                                                sx={{ backgroundColor:  '#66a321', color: isReadOnly ? undefined : '#fff', textTransform: 'none', fontWeight: 600, borderRadius: '5px', px: 2, py: 0.8, '&:hover': { backgroundColor: isReadOnly ? undefined : '#558a1b' } }}>
                                                                Add Product
                                                            </Button>
                                                        </Box>
                                                    )}
                                                    </Box>
                                                </Paper>
                                            </Box>
                                        )}
                                    </Paper>
                                );
                            })}

                            {companyFields.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 8, backgroundColor: '#f8fbfd', borderRadius: '16px', border: '2px dashed #d9e3f5', cursor: isReadOnly ? 'default' : 'pointer', transition: 'all 0.2s ease', ...(!isReadOnly && { '&:hover': { borderColor: '#076599', backgroundColor: '#eef5fc' } }) }}
                                    onClick={() => !isReadOnly && setAddCompanyModalOpen(true)}>
                                    <IconBuilding size={48} color="#990707" style={{ marginBottom: '12px', opacity: 0.7 }} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#495057', mb: 1 }}>No companies added yet</Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#6c757d', mb: 2.5, maxWidth: '400px', mx: 'auto' }}>
                                        Click here or use the "Add Company" button above to get started — fill in manually or upload a document.
                                    </Typography>
                                   <Typography sx={{ fontSize: '0.9rem', color: '#076599', fontWeight: 600, mt: 1 }}>
                                        Add Your First Company
                                    </Typography>
                                </Box>
                            )}

                            {/* Step 1 Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: companies?.some(c => c.expanded) ? 'space-between' : 'flex-end', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #e9ecef' }}>
                                {companies?.some(c => c.expanded) && (
                                    <Button variant="outlined" startIcon={<IconPlus size={18} />} onClick={() => setAddCompanyModalOpen(true)}
                                         disabled={isReadOnly}
                                         sx={{
                                            background: isReadOnly ? undefined : 'linear-gradient(135deg, #5a9907 0%, #05750b 100%)',
                                            '&:hover': { background: isReadOnly ? undefined : 'linear-gradient(135deg, #065280 0%, #043d5e 100%)' },
                                            textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 3, py: 1,
                                            boxShadow: isReadOnly ? 'none' : '0 4px 14px rgba(7,101,153,0.3)',
                                        }}>
                                        Add Company
                                    </Button>
                                )}
                                <Button variant="contained" disabled={companies?.length === 0} onClick={handleSaveStep1} endIcon={<IconArrowRight size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        backgroundColor: '#076599', '&:hover': { backgroundColor: '#065280' }
                                    }}>
                                    Save & Continue
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* ============================
                        STEP 2: Valuation Settings
                        ============================ */}
                    {activeStep === 1 && (
                        <Box>
                            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#2b2c2a', mb: 3 }}>Valuation Parameters</Typography>

                            {/* <Alert severity="info" sx={{ mb: 3, borderRadius: '10px', backgroundColor: '#e8f4fd', '& .MuiAlert-icon': { color: '#076599' } }}>
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#076599' }}>How Valuation Works</Typography>
                                <Typography sx={{ fontSize: '0.8rem', color: '#0a4a6e', mt: 0.5 }}>
                                    Product Valuation = Revenue x Revenue Multiple x Stage Multiplier<br />
                                    Company Valuation = Sum of Products x (1 + Platform Premium) x (1 + Execution Premium)<br />
                                    Total = Sum of All Companies
                                </Typography>
                            </Alert> */}

                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                {/* F&F Settings */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f0f9e8', border: '1px solid #c8e6c9' }} elevation={0}>
                                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>Friends & Family Settings</Typography>
                                        <Typography className="pv-label">Base Revenue Multiple</Typography>
                                        <Controller name="valuationParams.ffRevenueMultiple" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2,backgroundColor: '#ffffff' }}
                                                inputProps={{ step: 0.5 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Typography className="pv-label" sx={{ mb: 1 }}>Stage Multipliers</Typography>
                                        {stageOptions.map(stage => (
                                            <Box key={stage} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#555' }}>{stage}</Typography>
                                                <Controller name={`valuationParams.ffStageMultipliers.${stage}`} control={control}
                                                    render={({ field }) => <TextField {...field} type="number" size="small" className="pv-input" sx={{ width: '90px',backgroundColor: '#ffff' }}
                                                        inputProps={{ step: 0.1 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                            </Box>
                                        ))}
                                    </Paper>
                                </Grid>

                                {/* VC Settings */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 3, borderRadius: '12px', backgroundColor: '#e8f4fd', border: '1px solid #bbdefb' }} elevation={0}>
                                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>Venture Capital Settings</Typography>
                                        <Typography className="pv-label">Base Revenue Multiple</Typography>
                                        <Controller name="valuationParams.vcRevenueMultiple" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2,backgroundColor: '#ffffff' }}
                                                inputProps={{ step: 0.5 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Typography className="pv-label" sx={{ mb: 1 }}>Stage Multipliers</Typography>
                                        {stageOptions.map(stage => (
                                            <Box key={stage} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#555' }}>{stage}</Typography>
                                                <Controller name={`valuationParams.vcStageMultipliers.${stage}`} control={control}
                                                    render={({ field }) => <TextField {...field} type="number" size="small" className="pv-input" sx={{ width: '90px',backgroundColor: '#ffff' }}
                                                        inputProps={{ step: 0.1 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                            </Box>
                                        ))}
                                    </Paper>
                                </Grid>
                            </Grid>

                              {/* Platform Premium */}
                            <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e9ecef' }} elevation={0}>
                                <Typography className="pv-label">Platform Premium (%)</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Controller name="valuationParams.platformPremium" control={control}
                                        render={({ field }) => (
                                            <Slider {...field} min={0} max={50} sx={{ flex: 1, color: '#076599' }}
                                                onChange={(_, val) => field.onChange(val)} />
                                        )} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#2b2c2a', minWidth: '50px', textAlign: 'right' }}>
                                        {valuationParams.platformPremium}%
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', mt: 1 }}>
                                    Additional value from integrated platform, cross-sell capabilities, and synergies
                                </Typography>
                            </Paper>

                            {/* Execution Premium */}
                            <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e9ecef', mb: 3 }} elevation={0}>
                                <Typography className="pv-label">Execution Premium (%)</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Controller name="valuationParams.executionPremium" control={control}
                                        render={({ field }) => (
                                            <Slider {...field} min={0} max={50} sx={{ flex: 1, color: '#e65100' }}
                                                onChange={(_, val) => field.onChange(val)} />
                                        )} />
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#2b2c2a', minWidth: '50px', textAlign: 'right' }}>
                                        {valuationParams.executionPremium}%
                                    </Typography>
                                </Box>
                                <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', mt: 1 }}>
                                    Additional value from team execution capability, track record, and operational excellence
                                </Typography>
                            </Paper>


                            {/* Step 2 Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #e9ecef' }}>
                                <Button variant="outlined" onClick={() => {setActiveStep(0); fetchDocumentData();}}
                                    startIcon={<IconArrowLeft size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        borderColor: '#076599', color: '#fff',
                                        '&:hover': { borderColor: '#800606', backgroundColor: '#e97070' }
                                    }}>
                                    Back
                                </Button>
                                <Button variant="contained" onClick={handleSaveStep2} endIcon={<IconArrowRight size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        backgroundColor: '#076599', '&:hover': { backgroundColor: '#065280' }
                                    }}>
                                    Save & Continue
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* ============================
                        STEP 3: Fundraising
                        ============================ */}
                    {activeStep === 2 && (
                        <Box>
                            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#2b2c2a', mb: 3 }}>Fundraising Details</Typography>

                            <Grid container spacing={3} sx={{ mb: 3 }}>
                                {/* F&F Round */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 3, borderRadius: '12px', backgroundColor: '#f0f9e8', border: '1px solid #c8e6c9' }} elevation={0}>
                                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>Friends & Family Round</Typography>
                                        <Typography className="pv-label">Target Raise ($)</Typography>
                                        <Controller name="fundraising.ffTargetRaise" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2,backgroundColor: '#ffffff' }}
                                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Typography className="pv-label">Expected Dilution (%)</Typography>
                                        <Controller name="fundraising.ffExpectedDilution" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2,backgroundColor: '#ffffff' }}
                                                inputProps={{ step: 0.5 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Box sx={{ pt: 2, borderTop: '1px solid #a5d6a7' }}>
                                            <Typography sx={{ fontSize: '0.82rem', color: '#555' }}>Implied Post-Money</Typography>
                                            <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#2e7d32' }}>
                                                {formatCurrency(fundraising.ffExpectedDilution > 0 ? fundraising.ffTargetRaise / (fundraising.ffExpectedDilution / 100) : 0)}
                                            </Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* VC Round */}
                                <Grid item xs={12} md={6}>
                                    <Paper sx={{ p: 3, borderRadius: '12px', backgroundColor: '#e8f4fd', border: '1px solid #bbdefb' }} elevation={0}>
                                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>Venture Capital Round</Typography>
                                        <Typography className="pv-label">Target Raise Min ($)</Typography>
                                        <Controller name="fundraising.vcTargetRaiseMin" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2,backgroundColor: '#ffffff' }}
                                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Typography className="pv-label">Target Raise Max ($)</Typography>
                                        <Controller name="fundraising.vcTargetRaiseMax" control={control}
                                            render={({ field }) => <TextField {...field} type="number" fullWidth size="small" className="pv-input" sx={{ mb: 2 ,backgroundColor: '#ffffff' }}
                                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)} />} />
                                        <Typography className="pv-label">Expected Dilution (%)</Typography>
                                        <Controller name="fundraising.vcExpectedDilution" control={control}
                                            render={({ field }) => <TextField sx={{backgroundColor:'#fff'}} {...field} type="number" fullWidth size="small" className="pv-input"
                                                inputProps={{ step: 0.5 }} onChange={(e) => field.onChange(parseFloat(e.target.value) || 0) } />} />
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Use of Funds */}
                            <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }} elevation={0}>
                                <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>Use of Funds Distribution</Typography>
                                {Object.entries(useOfFundsLabels).map(([key, label]) => (
                                    <Box key={key} sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#555' }}>{label}</Typography>
                                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#2b2c2a' }}>{fundraising.useOfFunds?.[key] || 0}%</Typography>
                                        </Box>
                                        <Controller name={`fundraising.useOfFunds.${key}`} control={control}
                                            render={({ field }) => (
                                                <Slider {...field} min={0} max={100} sx={{ color: '#076599' }}
                                                    onChange={(_, val) => field.onChange(val)} />
                                            )} />
                                    </Box>
                                ))}
                                <Box sx={{ pt: 1.5, borderTop: '1px solid #d0d5dd', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700 }}>Total</Typography>
                                    <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: useOfFundsTotal === 100 ? '#2e7d32' : '#c53131' }}>
                                        {useOfFundsTotal}%
                                    </Typography>
                                </Box>
                            </Paper>

                            {/* Step 3 Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #e9ecef' }}>
                                <Button variant="outlined" onClick={() => setActiveStep(1)}
                                    startIcon={<IconArrowLeft size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        borderColor: '#076599', color: '#fff',
                                       '&:hover': { borderColor: '#800606', backgroundColor: '#e97070' }
                                    }}>
                                    Back
                                </Button>
                                <Button variant="contained" onClick={handleSaveStep3} endIcon={<IconArrowRight size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        backgroundColor: '#076599', '&:hover': { backgroundColor: '#065280' }
                                    }}>
                                    Save & Continue
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* ============================
                        STEP 4: Results / Summary
                        ============================ */}
                    {activeStep === 3 && (
                        <Box>
                            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#2b2c2a', mb: 3 }}>Valuation Results & Summary</Typography>

                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 4 }}>
                                {[
                                    { label: 'Friends & Family Valuation', value: formatCurrency(ffTotal), sub: 'Discounted for early supporters', bg: 'linear-gradient(135deg, #66a321 0%, #4a8a10 100%)' },
                                    { label: 'Venture Capital Valuation', value: formatCurrency(vcTotal), sub: 'Institutional round target', bg: 'linear-gradient(135deg, #076599 0%, #054d75 100%)' },
                                    { label: 'F&F Discount', value: `${actualDiscount}%`, sub: 'Early believer advantage', bg: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)' }
                                ].map((card, i) => (
                                    <Grid item xs={12} md={4} key={i}>
                                        <Paper sx={{ p: 3, borderRadius: '12px', background: card.bg, color: '#fff' }} elevation={2}>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, opacity: 0.9, mb: 0.5 }}>{card.label}</Typography>
                                            <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1.2 }}>{card.value}</Typography>
                                            <Typography sx={{ fontSize: '0.78rem', mt: 1, opacity: 0.75 }}>{card.sub}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Company Breakdown */}
                            {(companies || []).map((company, cIdx) => (
                                <Box key={cIdx} sx={{ mb: 4 }}>
                                    <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#2b2c2a', mb: 2 }}>{company.name || `Company ${cIdx + 1}`}</Typography>
                                    <TableContainer component={Paper} sx={{ borderRadius: '10px', border: '1px solid #e0e4e8', overflow: 'auto' }} elevation={0}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>Product</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>Stage</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>Current ARR</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>F&F Valuation</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6c757d', textTransform: 'uppercase' }}>VC Valuation</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(company.products || []).map((product, pIdx) => (
                                                    <TableRow key={pIdx} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{product.name || '-'}</TableCell>
                                                        <TableCell>
                                                            <Chip label={product.stage} size="small" sx={{
                                                                fontSize: '0.72rem', fontWeight: 600,
                                                                backgroundColor: product.stage === 'Commercial' ? '#e8f5e9' : product.stage === 'Early Traction' ? '#e3f2fd' : '#fff8e1',
                                                                color: product.stage === 'Commercial' ? '#2e7d32' : product.stage === 'Early Traction' ? '#1565c0' : '#f57f17'
                                                            }} />
                                                        </TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.85rem' }}>{formatCurrency(product.currentARR || 0)}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2e7d32' }}>{formatCurrency(calculateProductValuation(product, true))}</TableCell>
                                                        <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#076599' }}>{formatCurrency(calculateProductValuation(product, false))}</TableCell>
                                                    </TableRow>
                                                ))}
                                                <TableRow sx={{ backgroundColor: '#f1f4f7' }}>
                                                    <TableCell colSpan={2} sx={{ fontSize: '0.85rem', fontWeight: 700 }}>Subtotal (Products)</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + (p.currentARR || 0), 0))}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#2e7d32' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, true), 0))}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#076599' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, false), 0))}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell colSpan={3} sx={{ fontSize: '0.85rem' }}>Platform Premium ({valuationParams.platformPremium}%)</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#2e7d32' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, true), 0) * valuationParams.platformPremium / 100)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#076599' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, false), 0) * valuationParams.platformPremium / 100)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                                    <TableCell colSpan={3} sx={{ fontSize: '0.85rem' }}>Execution Premium ({valuationParams.executionPremium}%)</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#2e7d32' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, true), 0) * (1 + valuationParams.platformPremium / 100) * valuationParams.executionPremium / 100)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', color: '#076599' }}>
                                                        {formatCurrency((company.products || []).reduce((s, p) => s + calculateProductValuation(p, false), 0) * (1 + valuationParams.platformPremium / 100) * valuationParams.executionPremium / 100)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow sx={{ backgroundColor: '#1a237e' }}>
                                                    <TableCell colSpan={3} sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#fff' }}>COMPANY VALUATION</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#fff' }}>{formatCurrency(calculateCompanyValuation(company, true))}</TableCell>
                                                    <TableCell align="right" sx={{ fontSize: '0.85rem', fontWeight: 800, color: '#fff' }}>{formatCurrency(calculateCompanyValuation(company, false))}</TableCell>
                                                </TableRow>
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Box>
                            ))}

                            {/* Total Summary (multi-company) */}
                            {(companies || []).length > 1 && (
                                <Paper sx={{ p: 3, borderRadius: '12px', backgroundColor: '#1a237e', color: '#fff', mb: 3 }} elevation={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <Typography sx={{ fontSize: '0.8rem', opacity: 0.75 }}>Total Companies</Typography>
                                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>{companies.length}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography sx={{ fontSize: '0.8rem', opacity: 0.75 }}>Total F&F Valuation</Typography>
                                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>{formatCurrency(ffTotal)}</Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography sx={{ fontSize: '0.8rem', opacity: 0.75 }}>Total VC Valuation</Typography>
                                            <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>{formatCurrency(vcTotal)}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            )}

                            {/* Key Metrics */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                {[
                                    { label: 'Valuation Uplift', value: ffTotal > 0 ? `${((vcTotal / ffTotal - 1) * 100).toFixed(0)}%` : '0%', sub: 'From F&F to VC', bg: 'linear-gradient(135deg, #303f9f 0%, #1a237e 100%)' },
                                    { label: 'Total ARR (Current)', value: formatCurrency(getAllProducts().reduce((s, p) => s + (p.currentARR || 0), 0)), sub: `Across ${getAllProducts().length} products`, bg: 'linear-gradient(135deg, #c2185b 0%, #ad1457 100%)' },
                                    { label: 'Year 2 ARR (Projected)', value: formatCurrency(getAllProducts().reduce((s, p) => s + (p.year2ARR || 0), 0)), sub: 'Growth trajectory', bg: 'linear-gradient(135deg, #e65100 0%, #bf360c 100%)' }
                                ].map((card, i) => (
                                    <Grid item xs={12} md={4} key={i}>
                                        <Paper sx={{ p: 3, borderRadius: '12px', background: card.bg, color: '#fff' }} elevation={2}>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, opacity: 0.9, mb: 0.5 }}>{card.label}</Typography>
                                            <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, lineHeight: 1.2 }}>{card.value}</Typography>
                                            <Typography sx={{ fontSize: '0.78rem', mt: 1, opacity: 0.75 }}>{card.sub}</Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>

                            {/* Export Buttons */}
                            <Box sx={{ display: 'flex', gap: 2, pt: 3, borderTop: '1px solid #e9ecef', flexWrap: 'wrap' }}>
                                <Button variant="contained" fullWidth sx={{
                                    flex: 1, minWidth: '200px', py: 1.5, backgroundColor: '#66a321', '&:hover': { backgroundColor: '#558a1b' },
                                    textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontSize: '0.95rem'
                                }}>
                                    Generate F&F Investment Memo
                                </Button>
                                <Button variant="contained" fullWidth sx={{
                                    flex: 1, minWidth: '200px', py: 1.5, backgroundColor: '#076599', '&:hover': { backgroundColor: '#065280' },
                                    textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontSize: '0.95rem'
                                }}>
                                    Generate VC Full Dossier
                                </Button>
                            </Box>

                            {/* Step 4 Navigation */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, pt: 3, borderTop: '1px solid #e9ecef' }}>
                                <Button variant="outlined" onClick={() => setActiveStep(2)}
                                    startIcon={<IconArrowLeft size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        borderColor: '#076599', color: '#ffff',
                                        '&:hover': { borderColor: '#800606', backgroundColor: '#e97070' }
                                    }}>
                                    Back
                                </Button>
                                {/* <Button variant="contained" onClick={handleFinalSubmit} endIcon={<IconCheck size={18} />}
                                    sx={{
                                        textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 4, py: 1.2,
                                        backgroundColor: '#66a321', '&:hover': { backgroundColor: '#558a1b' }
                                    }}>
                                    Submit
                                </Button> */}
                            </Box>
                        </Box>
                    )}

                </Paper>
            </Box>
        </PageContainer>

        {/* Add Company Modal */}
        <Dialog
            open={addCompanyModalOpen}
            onClose={() => setAddCompanyModalOpen(false)}
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 300 }}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    maxWidth: '520px',
                    width: '100%',
                    overflow: 'hidden',
                    boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
                }
            }}
        >
            <Box sx={{
                background: 'linear-gradient(135deg, #076599 0%, #054d75 100%)',
                px: 3, py: 1.3, position: 'relative',
            }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>
                    Add a Company
                </Typography>
                <Typography sx={{ fontSize: '0.80rem', color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                    Choose how you'd like to add company details
                </Typography>
                <IconButton
                    onClick={() => setAddCompanyModalOpen(false)}
                    sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' } }}
                >
                    <IconX size={20} />
                </IconButton>
            </Box>
            <DialogContent sx={{ px: 3.5, py: 3.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Option 1: Manual Form */}
                    <Box
                        onClick={() => { appendCompany({ ...defaultCompany }); setAddCompanyModalOpen(false); }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 2.5,
                            p: 2.5, borderRadius: '14px', cursor: 'pointer',
                            border: '2px solid #e3eef5',
                            background: 'linear-gradient(145deg, #f8fbfd, #ffffff)',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                borderColor: '#076599',
                                background: 'linear-gradient(145deg, #eef5fc, #f4f8fb)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(7,101,153,0.12)',
                            },
                        }}
                    >
                        <Box sx={{
                            width: 52, height: 52, borderRadius: '14px',
                            background: 'linear-gradient(135deg, #076599 0%, #0a7bb8 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(7,101,153,0.25)',
                        }}>
                            <IconForms size={24} color="#fff" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#2b2c2a', mb: 0.3 }}>
                                Fill in Manually
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', lineHeight: 1.4 }}>
                                Add company details by filling out the form fields directly
                            </Typography>
                        </Box>
                        <IconArrowRight size={18} color="#adb5bd" />
                    </Box>

                    {/* Divider */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1 }}>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e3eef5' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#adb5bd', textTransform: 'uppercase', letterSpacing: '0.5px' }}>or</Typography>
                        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e3eef5' }} />
                    </Box>

                    {/* Option 2: Upload Document */}
                    <Box
                        onClick={() => { topLevelFileInputRef.current?.click(); }}
                        sx={{
                            display: 'flex', alignItems: 'center', gap: 2.5,
                            p: 2.5, borderRadius: '14px', cursor: 'pointer',
                            border: '2px solid #e3eef5',
                            background: 'linear-gradient(145deg, #f8fbfd, #ffffff)',
                            transition: 'all 0.25s ease',
                            '&:hover': {
                                borderColor: '#66a321',
                                background: 'linear-gradient(145deg, #f0f9e8, #f8fbf4)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 8px 24px rgba(102,163,33,0.12)',
                            },
                        }}
                    >
                        <Box sx={{
                            width: 52, height: 52, borderRadius: '14px',
                            background: 'linear-gradient(135deg, #66a321 0%, #7bb830 100%)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(102,163,33,0.25)',
                        }}>
                            <IconFileText size={24} color="#fff" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#2b2c2a', mb: 0.3 }}>
                                Upload Document
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#6c757d', lineHeight: 1.4 }}>
                                Auto-fill company details from PDF & DOCX
                            </Typography>
                        </Box>
                        <IconArrowRight size={18} color="#adb5bd" />
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>

        {/* Scroll to Top Button */}
        <Zoom in={showScrollTop}>
            <IconButton
                onClick={scrollToTop}
                sx={{
                    position: 'fixed',
                    bottom: 32,
                    right: 32,
                    width: 48,
                    height: 48,
                    backgroundColor: '#07659c',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(7, 101, 156, 0.4)',
                    '&:hover': {
                        backgroundColor: '#054a73',
                    },
                    zIndex: 1000,
                }}
            >
                <IconArrowUp size={22} />
            </IconButton>
        </Zoom>
       </div>
    );
}
